import { useQuery } from '@tanstack/react-query';
import Select, { ValueContainerProps, components } from 'react-select';
import ProductCards from '../components/ProductCards';
import { getAccessories } from '../service/getAccessories';
import { useContext, useState } from 'react';
import {
  NonScriptRangeProps,
  ReactSelectOptionProp,
} from '../interface/interface';
import { useAuth } from 'react-oidc-context';
import showToast from './ToastComponent';
import { CartContext } from '../context/cartContext';
import { getProductFormulations } from '../query/getProductFormulationQuery';
import { getProductBrands } from '../query/getProductBrandQuery';
import LoadingWrapper from './LoadingWrapper';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ValueContainer = ({ children, ...props }: ValueContainerProps<any>) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let [values, input] = children as any;

  if (Array.isArray(values)) {
    const plural = values.length === 1 ? '' : 's';
    values = `${values.length} item${plural} selected`;
  }

  return (
    <components.ValueContainer {...props}>
      {values}
      {input}
    </components.ValueContainer>
  );
};

export function NonScriptRangeProducts(props: NonScriptRangeProps) {
  const [selectedBrand, setSelectedBrand] = useState<
    Array<ReactSelectOptionProp>
  >([]);
  const [selectedForm, setSelectedForm] = useState<
    Array<ReactSelectOptionProp>
  >([]);

  const { user } = useAuth();
  const { cart } = useContext(CartContext);

  const { data, isLoading } = useQuery({
    queryKey: [
      'accessories',
      user?.access_token,
      selectedBrand,
      selectedForm,
      cart,
    ],
    queryFn: async () => {
      const accessories = await getAccessories(
        user!.access_token,
        selectedBrand,
        selectedForm,
        cart,
      );
      if (!accessories)
        showToast({
          message: 'Unable to fetch accessories!',
          type: 'info',
        });
      else return accessories;
      return [];
    },
    enabled: !!user?.access_token,
  });

  const { brands, isBrandLoading } = getProductBrands(user?.access_token);

  const { formulation, isFormLoading } = getProductFormulations(
    user?.access_token,
  );

  const needsLoading =
    isLoading || isBrandLoading || isFormLoading || props.loading;

  return (
    <LoadingWrapper state={needsLoading}>
      <div className="result-block-container">
        <div className="flex-row md:flex justify-start md:justify-end">
          <div className="flex-row md:flex items-center mb-2 md:mb-5 me-5">
            <small>Brand</small>
            <Select
              classNames={{
                control: (state) =>
                  `${
                    state.isFocused
                      ? 'border-grey-400 border text-sm'
                      : 'border-grey-400 border text-sm'
                  } w-60 cursor-pointer md:ml-2`,
              }}
              value={selectedBrand}
              options={brands || []}
              onChange={(selected) => {
                setSelectedBrand([...selected]);
              }}
              isSearchable
              hideSelectedOptions={false}
              isMulti
              isClearable
              components={{ ValueContainer }}
              isLoading={isBrandLoading}
            />
          </div>
          <div className="flex-row md:flex items-center mb-5">
            <small>Form</small>
            <Select
              classNames={{
                control: (state) =>
                  `${
                    state.isFocused
                      ? 'border-grey-400 border text-sm'
                      : 'border-grey-400 border text-sm'
                  } w-60 cursor-pointer md:ml-2`,
              }}
              value={selectedForm}
              options={formulation || []}
              onChange={(selected) => {
                setSelectedForm([...selected]);
              }}
              isSearchable
              hideSelectedOptions={false}
              isMulti
              isClearable
              components={{ ValueContainer }}
              isLoading={isBrandLoading}
            />
          </div>
        </div>

        <ul className="grid grid-cols-1 gap-6 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-4">
          {data && data?.length > 0 ? (
            data?.map((product: any, index: number) => (
              <div key={index}>
                <ProductCards
                  addProductToCart={async (id: string) => {
                    await props.addToCartMutation.mutateAsync(id);
                  }}
                  removeProductFromCart={async (productId: string) => {
                    await props.removeFromCartMutation.mutateAsync(productId);
                  }}
                  product={product}
                  substitueCard={false}
                  isAccessories={true}
                  allowedProduct={false}
                />
              </div>
            ))
          ) : (
            <p>No Non-Script Range Products Exist </p>
          )}
        </ul>
      </div>
    </LoadingWrapper>
  );
}
